export type FrameabilityResult = {
  status: 'OK' | 'BLOCKED' | 'UNKNOWN';
  reason: string | null;
};

type ProviderMetadata = {
  id: string;
  supportsEmbed: boolean;
  frameSrcOrigins?: string[];
};

type OverlayEvent =
  | 'modal_opened'
  | 'modal_closed'
  | 'iframe_injected'
  | 'embed_loaded'
  | 'embed_first_paint'
  | 'link_out_fallback'
  | 'embed_blocked';

type OverlayEventPayload = {
  providerId?: string;
  url?: string;
  status?: string;
  reason?: string | null;
  mode?: 'meeting' | 'service';
  durationMs?: number;
};

type InitializeBookingOverlayOptions = {
  root: HTMLElement;
  getProvider?: (providerId: string) => ProviderMetadata | undefined;
  fetchFrameability?: (providerId: string, url: string) => Promise<FrameabilityResult>;
  onEvent?: (event: OverlayEvent, payload: OverlayEventPayload) => void;
  shouldForceEmbed?: (context: {
    providerId?: string;
    url?: string;
    mode: 'meeting' | 'service';
  }) => boolean;
};

type CleanupFn = () => void;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const preconnectedOrigins = new Set<string>();
let activeModalCloser: ((fromHistory?: boolean) => void) | null = null;
let pendingHistoryBack = 0;

function ensurePreconnect(origin: string) {
  if (!origin || preconnectedOrigins.has(origin)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = origin;
  link.crossOrigin = '';
  document.head.appendChild(link);
  preconnectedOrigins.add(origin);
}

function trapFocus(modal: HTMLElement, event: KeyboardEvent) {
  if (event.key !== 'Tab') {
    return;
  }

  const focusable = Array.from(
    modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter((el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true');

  if (focusable.length === 0) {
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!event.shiftKey && document.activeElement === last) {
    first.focus();
    event.preventDefault();
    return;
  }

  if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
    last.focus();
    event.preventDefault();
  }
}

function getClosestActionElement(node: Element | null): HTMLElement | null {
  let current: Element | null = node;
  while (current) {
    if (current instanceof HTMLElement && current.classList.contains('tap-actions__item')) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

export function initializeBookingOverlay(options: InitializeBookingOverlayOptions): () => void {
  const { root, getProvider, fetchFrameability, onEvent, shouldForceEmbed } = options;
  const cleanupFns: CleanupFn[] = [];
  let disposed = false;

  if (!root) {
    return () => undefined;
  }

  const items = Array.from(
    root.querySelectorAll<HTMLElement>('.tap-actions__item--booking, .tap-actions__item--service')
  );

  if (!items.length) {
    return () => undefined;
  }

  const popstateHandler = () => {
    if (pendingHistoryBack) {
      pendingHistoryBack -= 1;
      return;
    }
    if (activeModalCloser) activeModalCloser(true);
  };

  window.addEventListener('popstate', popstateHandler);
  cleanupFns.push(() => {
    window.removeEventListener('popstate', popstateHandler);
  });

  items.forEach((item) => {
    const toggle = item.querySelector<HTMLInputElement>('input.tap-booking-toggle');
    const modal = item.querySelector<HTMLElement>('.tap-booking-modal');
    const dialog = modal?.querySelector<HTMLElement>('.tap-booking-modal__dialog');
    let closeLabel = modal?.querySelector<HTMLElement>('.tap-booking-modal__close');
    const backdrop = modal?.querySelector<HTMLElement>('.tap-booking-modal__backdrop');
    const externalLink = modal?.querySelector<HTMLAnchorElement>('.tap-booking-modal__footer a');
    const iframe = modal?.querySelector<HTMLIFrameElement>('iframe');
    let cta = item.querySelector<HTMLElement>('.tap-action');
    const providerId = item.getAttribute('data-action-provider') ?? undefined;

    if (!toggle || !modal || !dialog || !cta) {
      return;
    }

    modal.hidden = true;
    modal.inert = true;
    modal.setAttribute('aria-hidden', 'true');

    // Legacy published labels become functional links while approval is pending.
    if (cta.tagName === 'LABEL' && externalLink) {
      const anchor = document.createElement('a');
      Array.from(cta.attributes).forEach(attr => {
        if (!['for', 'role', 'tabindex', 'aria-haspopup', 'aria-controls'].includes(attr.name)) anchor.setAttribute(attr.name, attr.value);
      });
      anchor.href = externalLink.href;
      anchor.target = '_blank';
      anchor.rel = 'noopener';
      anchor.innerHTML = cta.innerHTML;
      cta.replaceWith(anchor);
      cta = anchor;
    }

    // Upgrade only after the existing provider/frameability policy accepts embedding.
    const enableNativeControls = () => {
      if (disposed || !cta || !cta.isConnected) return;
      if (cta.tagName !== 'BUTTON') {
        const button = document.createElement('button');
        Array.from(cta.attributes).forEach(attr => {
          if (!['href', 'target', 'rel', 'for', 'role', 'tabindex'].includes(attr.name)) button.setAttribute(attr.name, attr.value);
        });
        button.type = 'button';
        button.innerHTML = cta.innerHTML;
        cta.replaceWith(button);
        cta = button;
      }
      cta.setAttribute('aria-haspopup', 'dialog');
      cta.setAttribute('aria-controls', modal.id);
      cta.setAttribute('aria-expanded', 'false');
      if (closeLabel && closeLabel.tagName !== 'BUTTON') {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = closeLabel.className;
        button.setAttribute('aria-label', 'Close booking dialog');
        button.innerHTML = closeLabel.innerHTML;
        closeLabel.replaceWith(button);
        closeLabel = button;
      }
      cta.addEventListener('click', openModal);
      cleanupFns.push(() => cta?.removeEventListener('click', openModal));
      if (closeLabel) {
        closeLabel.addEventListener('click', closeHandler);
        cleanupFns.push(() => closeLabel?.removeEventListener('click', closeHandler));
      }
      if (provider && provider.frameSrcOrigins && provider.frameSrcOrigins.length > 0) {
        const preconnectOrigins = provider.frameSrcOrigins;
        const pointerListener = () => {
          preconnectOrigins.forEach((origin) => ensurePreconnect(origin));
        };
        cta?.addEventListener('pointerenter', pointerListener, { once: true });
        cta?.addEventListener('focus', pointerListener, { once: true });
        cleanupFns.push(() => {
          cta?.removeEventListener('pointerenter', pointerListener);
          cta?.removeEventListener('focus', pointerListener);
        });
      }
      const footer = modal.querySelector('footer.tap-booking-modal__footer');
      footer?.setAttribute('role', 'presentation');
    };

    const sanitizedUrl = iframe?.getAttribute('src') ?? undefined;
    const bookingMode: 'meeting' | 'service' = item.classList.contains('tap-actions__item--service')
      ? 'service'
      : 'meeting';

    const provider = providerId && getProvider ? getProvider(providerId) : undefined;
    const normalizedUrl = sanitizedUrl?.trim() ?? undefined;

    const isForceEmbed = (): boolean => {
      if (!shouldForceEmbed) {
        return false;
      }
      return !!shouldForceEmbed({
        providerId,
        url: normalizedUrl,
        mode: bookingMode
      });
    };

    if (provider && provider.supportsEmbed === false) {
      downgradeToLink(item, sanitizedUrl, onEvent, providerId, 'provider_no_embed', 'UNKNOWN', bookingMode);
      return;
    }

    let frameabilityStatus: FrameabilityResult | null = null;
    let pendingFrameability = false;

    if (providerId && sanitizedUrl && fetchFrameability) {
      pendingFrameability = true;
      fetchFrameability(providerId, sanitizedUrl)
        .then((result) => {
          if (disposed) return;
          frameabilityStatus = result;
          if (result.status === 'OK') {
            item.setAttribute('data-frameability', 'embed');
            item.removeAttribute('data-embed-fallback');
            enableNativeControls();
            return;
          }

          if (isForceEmbed()) {
            item.setAttribute('data-frameability', 'forced');
            item.setAttribute('data-embed-fallback', result.reason ?? 'forced');
            enableNativeControls();
            return;
          }

          downgradeToLink(
            item,
            sanitizedUrl,
            onEvent,
            providerId,
            result.reason ?? null,
            result.status,
            bookingMode
          );
        })
        .catch(() => {
          if (disposed) return;
          frameabilityStatus = { status: 'UNKNOWN', reason: 'frameability_fetch_failed' };
          if (isForceEmbed()) {
            item.setAttribute('data-frameability', 'forced');
            item.setAttribute('data-embed-fallback', frameabilityStatus.reason ?? 'forced');
            enableNativeControls();
          } else {
            downgradeToLink(
              item,
              sanitizedUrl,
              onEvent,
              providerId,
              frameabilityStatus.reason,
              frameabilityStatus.status,
              bookingMode
            );
          }
        })
        .finally(() => {
          pendingFrameability = false;
        });
    }


    let previousActive: HTMLElement | null = null;
    let historyStateActive = false;
    let iframeLoadListenerAttached = false;
    let iframeLoaded = false;
    let modalOpenedAt: number | null = null;

    const keydownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      trapFocus(dialog, event);
    };

    const openModal = () => {
      if (modal.getAttribute('data-open') === 'true') return;
      if (pendingFrameability && frameabilityStatus === null) {
        return;
      }

      if (isForceEmbed()) {
        item.setAttribute('data-frameability', 'forced');
      }

      if (frameabilityStatus && frameabilityStatus.status !== 'OK' && !isForceEmbed()) {
        downgradeToLink(
          item,
          sanitizedUrl,
          onEvent,
          providerId,
          frameabilityStatus.reason ?? null,
          frameabilityStatus.status,
          bookingMode
        );
        return;
      }

      if (activeModalCloser && activeModalCloser !== closeModal) {
        activeModalCloser(true);
      }

      previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modalOpenedAt = typeof performance !== 'undefined' ? performance.now() : null;
      document.body.dataset.inktapModalOpen = 'true';
      document.body.style.setProperty('overflow', 'hidden');
      toggle.checked = true;
      modal.hidden = false;
      modal.inert = false;
      modal.removeAttribute('aria-hidden');
      cta?.setAttribute('aria-expanded', 'true');
      modal.setAttribute('data-open', 'true');
      activeModalCloser = closeModal;

      dialog.setAttribute('tabindex', '-1');
      dialog.focus({ preventScroll: true });

      document.addEventListener('keydown', keydownHandler);
      const iframeElement = iframe;
      if (iframeElement && !iframeLoaded && !iframeLoadListenerAttached) {
        iframeLoadListenerAttached = true;
        onEvent?.('iframe_injected', {
          providerId,
          url: sanitizedUrl ?? undefined,
          mode: bookingMode
        });
        const onIframeLoad = () => {
            if (disposed) return;
            iframeLoaded = true;
            const elapsed = modalOpenedAt ? Math.max(performance.now() - modalOpenedAt, 0) : undefined;
            onEvent?.('embed_loaded', {
              providerId,
              url: sanitizedUrl ?? undefined,
              mode: bookingMode,
              durationMs: elapsed
            });
            if (modalOpenedAt !== null) {
              requestAnimationFrame(() => {
                if (disposed || modalOpenedAt === null) return;
                onEvent?.('embed_first_paint', {
                  providerId,
                  url: sanitizedUrl ?? undefined,
                  mode: bookingMode,
                  durationMs: Math.max(performance.now() - modalOpenedAt!, 0)
                });
              });
            }
        };
        iframeElement.addEventListener('load', onIframeLoad, { once: true });
        cleanupFns.push(() => iframeElement.removeEventListener('load', onIframeLoad));
      }

      if (window.history && typeof window.history.pushState === 'function') {
        const stateKey = String(Date.now()) + ':' + (providerId || '');
        window.history.pushState({ __inktapBookingModal: stateKey }, document.title);
        historyStateActive = true;
      }

      onEvent?.('modal_opened', {
        providerId,
        url: sanitizedUrl ?? undefined,
        mode: bookingMode
      });
    };

    const closeModal = (fromHistory?: boolean) => {
      if (modal.getAttribute('data-open') !== 'true') return;
      if (toggle.checked) {
        toggle.checked = false;
      }
      modal.removeAttribute('data-open');
      modal.hidden = true;
      modal.inert = true;
      modal.setAttribute('aria-hidden', 'true');
      cta?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', keydownHandler);

      if (document.body.dataset.inktapModalOpen) {
        delete document.body.dataset.inktapModalOpen;
        document.body.style.removeProperty('overflow');
      }

      if (previousActive && typeof previousActive.focus === 'function') {
        previousActive.focus({ preventScroll: true });
      }

      if (historyStateActive && window.history && !fromHistory) {
        pendingHistoryBack += 1;
        window.history.back();
      }
      historyStateActive = false;
      activeModalCloser = null;

      onEvent?.('modal_closed', {
        providerId,
        url: sanitizedUrl ?? undefined,
        mode: bookingMode,
        durationMs: modalOpenedAt ? Math.max(performance.now() - modalOpenedAt, 0) : undefined
      });
      modalOpenedAt = null;
    };

    const changeHandler = () => {
      if (toggle.checked) {
        openModal();
      } else {
        closeModal();
      }
    };

    toggle.addEventListener('change', changeHandler);
    cleanupFns.push(() => toggle.removeEventListener('change', changeHandler));

    if (backdrop) {
      const backdropClickHandler = () => closeModal();
      backdrop.addEventListener('click', backdropClickHandler);
      cleanupFns.push(() => backdrop.removeEventListener('click', backdropClickHandler));
    }

    const closeHandler = (event: Event) => {
      event.preventDefault();
      closeModal();
    };

    if (externalLink) {
      const externalHandler = () => {
        onEvent?.('link_out_fallback', {
          providerId,
          url: externalLink.href,
          reason: 'open_externally'
        });
        closeModal();
      };
      externalLink.addEventListener('click', externalHandler);
      cleanupFns.push(() => externalLink.removeEventListener('click', externalHandler));
    }

    if (!pendingFrameability) enableNativeControls();

    cleanupFns.push(() => {
      if (activeModalCloser === closeModal) {
        activeModalCloser(true);
      }
    });
  });

  return () => {
    disposed = true;
    cleanupFns.forEach((fn) => fn());
  };
}

function downgradeToLink(
  item: HTMLElement,
  url: string | undefined,
  onEvent?: InitializeBookingOverlayOptions['onEvent'],
  providerId?: string,
  reason?: string | null,
  status?: FrameabilityResult['status'],
  mode?: 'meeting' | 'service'
) {
  const existing = item.querySelector<HTMLAnchorElement>('a.tap-action');
  if (existing && !item.querySelector('.tap-booking-modal')) {
    return;
  }

  const toggle = item.querySelector<HTMLInputElement>('input.tap-booking-toggle');
  const modal = item.querySelector<HTMLElement>('.tap-booking-modal');
  const label = item.querySelector<HTMLElement>('.tap-action');

  if (!label || !url) {
    return;
  }

  const anchor = document.createElement('a');
  anchor.className = label.className || 'tap-action';
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  anchor.innerHTML = label.innerHTML;

  label.replaceWith(anchor);

  if (toggle) {
    toggle.remove();
  }
  if (modal) {
    modal.remove();
  }

  item.setAttribute('data-frameability', 'link');
  item.setAttribute('data-embed-fallback', reason || 'fallback');

  if (status === 'BLOCKED') {
    onEvent?.('embed_blocked', {
      providerId,
      url,
      status,
      reason: reason ?? null,
      mode
    });
  }

  onEvent?.('link_out_fallback', {
    providerId,
    url,
    reason: reason ?? 'fallback',
    status,
    mode
  });
}

export function bindBookingOverlayFromEvent(event: Event, options: InitializeBookingOverlayOptions) {
  const actionElement = getClosestActionElement(event.target as Element | null);
  if (!actionElement) {
    return;
  }
  initializeBookingOverlay({ ...options, root: actionElement });
}
